import { Route } from "../types";
import { checkLoggedIn } from "../services/cognito";
import { isProfileComplete } from "../services/profile";
import { Pages } from "../pages/base";
import LoginPage from "../pages/login";
import RegisterPage from "../pages/register";
import HomePage from "../pages/home";
import NotFoundPage from "../pages/notfound";
import ProfilePage from "../pages/profile";
import ProfilerPage from "../pages/profiler";

// Import loading template
const pageLoadingTemplate = `
<div class="mx-auto px-2">
  <div class="container mx-auto w-80">
    <div class="flex justify-center py-2">
      <img src="/img/ripple.svg" class="h-16" />
    </div>
    <div class="text-center text-gray-600 mt-4">
      <p>Loading...</p>
    </div>
  </div>
</div>
`;

export class Router {
  private routes: Route[] = [];
  private defaultRoute: Route;
  private loginRoute: Route;
  private container: HTMLElement;

  constructor(containerEl: HTMLElement, defaultRoute: Route, loginRoute: Route) {
    this.container = containerEl;
    this.defaultRoute = defaultRoute;
    this.loginRoute = loginRoute;
    this.handlePopState = this.handlePopState.bind(this);
    window.addEventListener("popstate", this.handlePopState);
  }

  addRoute(route: Route): void {
    this.routes.push(route);
  }

  private showLoading(): void {
    this.container.innerHTML = pageLoadingTemplate;
  }

  async navigate(path: string): Promise<void> {
    await this.handleRouting(path, true);
  }

  private async handlePopState(): Promise<void> {
    await this.handleRoute(window.location.pathname);
  }

  private async navigationGuard(isLoggedIn: boolean, path: string): Promise<string> {
    const currentPath = path.replace(/\/$/, "");
    const allowedUnAuthPaths = ["/login", "/register", "/404"];
    const disabledAuthPaths = ["/login", "/register"];
    let newPath = path;

    if (currentPath === "/") {
      if (!isLoggedIn) {
        newPath = "/login";
      } else {
        newPath = "/";
      }
    }

    if (!isLoggedIn && !allowedUnAuthPaths.includes(currentPath)) {
      console.log(`not logged in and path is not an allowed unauth path. redirecting to /login.`);
      newPath = "/login";
    }

    if (isLoggedIn && disabledAuthPaths.includes(currentPath)) {
      console.log(`logged in and path is in the disabled auth paths. redirecting to /.`);
      newPath = "/";
    }

    if (path != newPath) {
      window.location.href = newPath;
    }
    return newPath;
  }

  private async handleRoute(path: string): Promise<void> {
    await this.handleRouting(path, false);
  }

  private async handleRouting(path: string, isProgrammatic: boolean): Promise<void> {
    // Show loading immediately
    this.showLoading();
    
    const isLoggedIn = await checkLoggedIn();
    
    // Apply navigation guard only for non-programmatic navigation
    if (!isProgrammatic) {
      path = await this.navigationGuard(isLoggedIn, path);
      console.log(path);
    }
    
    const route = this.routes.find((r) => r.path === path) || this.defaultRoute;

    if (route.protected) {
      if (!isLoggedIn) {
        sessionStorage.setItem("redirectPath", path);
        const historyMethod = isProgrammatic ? 'pushState' : 'replaceState';
        window.history[historyMethod]({}, "", this.loginRoute.path);
        this.renderRoute(this.loginRoute);
        return;
      }

      // Check if user has completed their wine profile
      const profileComplete = await isProfileComplete();
      
      if (path === "/profiler") {
        // Check if user is intentionally resetting their profile
        const bypassProfileCheck = sessionStorage.getItem('bypassProfileCheck');
        if (bypassProfileCheck) {
          // Clear the bypass flag after use
          sessionStorage.removeItem('bypassProfileCheck');
          console.log("Bypassing profile completion check - user is resetting preferences");
        } else if (profileComplete) {
          // If user is trying to access profiler but profile is already complete, redirect to home
          console.log("Profile already complete, redirecting to home");
          const historyMethod = isProgrammatic ? 'pushState' : 'replaceState';
          window.history[historyMethod]({}, "", "/");
          const homeRoute = this.routes.find((r) => r.path === "/");
          if (homeRoute) {
            this.renderRoute(homeRoute);
            return;
          }
        }
      } else {
        // If user is trying to access any other page but profile is incomplete, redirect to profiler
        if (!profileComplete) {
          console.log("Profile incomplete, redirecting to profiler");
          const historyMethod = isProgrammatic ? 'pushState' : 'replaceState';
          window.history[historyMethod]({}, "", "/profiler");
          const profilerRoute = this.routes.find((r) => r.path === "/profiler");
          if (profilerRoute) {
            this.renderRoute(profilerRoute);
            return;
          }
        }
      }
    }

    const historyMethod = isProgrammatic ? 'pushState' : 'replaceState';
    window.history[historyMethod]({}, "", path);
    this.renderRoute(route);
  }

  private renderRoute(route: Route): void {
    this.container.innerHTML = "";

    console.log(`Rendering route: ${route.pageName}`);

    switch (route.pageName) {
      case Pages.LOGIN:
        new LoginPage();
        break;
      case Pages.REGISTER:
        new RegisterPage();
        break;
      case Pages.HOME:
        new HomePage();
        break;
      case Pages.PROFILE:
        new ProfilePage();
        break;
      case Pages.PROFILER:
        new ProfilerPage();
        break;
      default:
        new NotFoundPage();
        break;
    }
  }

  /**
   * Call this after successful login to redirect to the originally requested page
   */
  redirectToSavedPath(): void {
    const redirectPath = sessionStorage.getItem("redirectPath");
    if (redirectPath) {
      sessionStorage.removeItem("redirectPath");
      this.navigate(redirectPath);
    } else {
      this.navigate("/");
    }
  }

  init(): void {
    this.handleRoute(window.location.pathname);
  }

  destroy(): void {
    window.removeEventListener("popstate", this.handlePopState);
  }
}
